/**
 * Room Types Page
 * Display all room types for browsing (without dates)
 */

import { t, formatCurrency } from '../i18n';
import { Header, LoadingSpinner, ErrorMessage, HotelCard } from '../components';
import { getRoomTypes, getRoomImages, getHotelInfo, type RoomTypeInfo } from '../api';
import { getHotelCode, getTenantId } from '../config';
import { navigateTo } from '../store';
import { translateRoomType, translateRoomDescription, translateAmenity } from '../i18n';
import { closeWindow, isInLineApp } from '../liff';
import { openLightbox } from '../components/lightbox';

// Local state for this page
let roomTypesData: RoomTypeInfo[] = [];
let isLoading = true;
let error: string | null = null;

/**
 * Render room types page
 */
export function renderRoomTypesPage(): string {
  if (isLoading) {
    return `
      ${Header(t('roomTypesTitle'), true, true)}
      <div class="page-content">
        ${LoadingSpinner(t('loading'))}
      </div>
    `;
  }

  if (error) {
    return `
      ${Header(t('roomTypesTitle'), true, true)}
      <div class="page-content">
        ${ErrorMessage(error)}
      </div>
    `;
  }

  if (roomTypesData.length === 0) {
    return `
      ${Header(t('roomTypesTitle'), true, true)}
      <div class="page-content">
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <p>${t('noRoomsAvailable')}</p>
        </div>
      </div>
    `;
  }

  // Room type cards
  const roomCards = roomTypesData.map((room, index) => RoomTypeCard(room, index)).join('');

  return `
    ${Header(t('roomTypesTitle'), true, true)}
    <div class="page-content room-types-page">
      <div id="hotel-card-container">${HotelCard()}</div>
      <div class="page-intro">
        <h2 class="intro-title">${t('ourRooms')}</h2>
      </div>
      <div class="rooms-list" id="rooms-list">
        ${roomCards}
      </div>
    </div>

    <!-- Floating scroller (Top / Bottom) -->
    <div id="rooms-scroller" style="position:fixed; right:16px; bottom:24px; display:flex; flex-direction:column; gap:8px; z-index:2000;">
      <button id="rooms-scroll-to-top" aria-label="Scroll to top" style="display:none; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▲</button>
      <button id="rooms-scroll-to-bottom" aria-label="Scroll to bottom" style="display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▼</button>
    </div>
  `;
}

/**
 * Room Type Card Component (similar to RoomCard but without rate plan details)
 */
function RoomTypeCard(room: RoomTypeInfo, index: number): string {
  // Get translated names
  const roomTypeName = translateRoomType(room.roomTypeCode, room.name);
  const translatedDescription = translateRoomDescription(room.roomTypeCode, room.description);

  // Amenities (first 6)
  const amenitiesHtml = (room.amenities || [])
    .slice(0, 6)
    .map(amenity => {
      const translated = translateAmenity(amenity);
      return `<span class="amenity-tag">${translated}</span>`;
    })
    .join('');

  // Base rate display (++ indicates excluding taxes & service charge)
  const baseRateHtml =
    room.baseRate > 0
      ? `<div class="room-price">
        <span class="price-label">${t('startingFrom')}</span>
        <span class="price-amount">${formatCurrency(room.baseRate, room.currency)}<sup>++</sup></span>
        <span class="price-unit">${t('perNight')}</span>
        <div class="price-note">${t('excludingTaxes')}</div>
      </div>`
      : '';

  return `
    <div class="card room-card room-type-card" data-index="${index}">
      <div class="room-images" data-room-type="${room.roomTypeCode}">
        <div class="room-image-placeholder">
          <div class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
      
      <div class="room-info">
        <h3 class="room-name">${roomTypeName}</h3>
        ${room.bedType ? `<div class="room-bed-type" style="font-size:12px; color:#555; font-weight:600; margin-top:2px; margin-bottom:6px;">🛏️ ${room.bedType}${room.bedTypeDesc ? ` (${room.bedTypeDesc})` : ''}</div>` : ''}
        <p class="room-description">${translatedDescription}</p>
        
        <div class="room-amenities">
          ${amenitiesHtml}
        </div>
        
        ${baseRateHtml}
        
        <button class="btn btn-primary btn-check-availability" onclick="window.goToBooking(${index})">
          🔍 ${t('checkAvailability')}
        </button>
      </div>
    </div>
  `;
}

/**
 * Initialize room types page
 */
export async function initRoomTypesPage(): Promise<void> {
  console.log('[RoomTypes] Initializing...');

  // Load hotel info (logo, name, rating, address) - language-aware (resolves runtime hotelCode!)
  await loadHotelInfo();

  // Load room types data
  await loadRoomTypes();

  // Back button handler - close LIFF
  window.handleBack = () => {
    // Close the LIFF window
    if (isInLineApp()) {
      closeWindow();
    } else {
      // Fallback: go back or navigate to search
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigateTo('search');
      }
    }
  };

  // Go to booking handler
  window.goToBooking = (_index: number) => {
    // Navigate to search page to start booking flow
    navigateTo('search');
  };

  // Setup scroll handlers
  setupScrollHandlers();

  // Load image carousels
  await loadRoomCarousels();
}

/**
 * Load hotel info from API and update HotelCard (language-aware)
 */
async function loadHotelInfo(): Promise<void> {
  const container = document.getElementById('hotel-card-container');
  if (!container) return;

  try {
    const hotelInfo = await getHotelInfo();
    if (hotelInfo) {
      container.innerHTML = HotelCard({
        name: hotelInfo.hotelName,
        address: hotelInfo.hotelAddress,
        rating: hotelInfo.hotelRating,
        image: hotelInfo.hotelImage,
      });
    }
  } catch (err) {
    console.error('[RoomTypes] Failed to load hotel info:', err);
  }
}

/**
 * Load room types from API
 */
async function loadRoomTypes(): Promise<void> {
  isLoading = true;
  error = null;

  try {
    const hotelCode = getHotelCode();
    const result = await getRoomTypes(hotelCode);

    if (result && result.roomTypes) {
      roomTypesData = result.roomTypes;
    } else {
      error = t('errorGeneric');
    }
  } catch (err) {
    console.error('[RoomTypes] Load error:', err);
    error = t('errorNetwork');
  } finally {
    isLoading = false;
    // Re-render the page
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = renderRoomTypesPage();
      // Re-setup after render
      setupScrollHandlers();
      loadRoomCarousels();
      loadHotelInfo();
    }
  }
}

/**
 * Setup scroll handlers
 */
function setupScrollHandlers(): void {
  const scrollerTop = document.getElementById('rooms-scroll-to-top') as HTMLButtonElement | null;
  const scrollerBottom = document.getElementById(
    'rooms-scroll-to-bottom'
  ) as HTMLButtonElement | null;
  const listEl = document.getElementById('rooms-list');

  if (!listEl) return;

  function updateScrollerVisibility() {
    if (!scrollerTop || !scrollerBottom || !listEl) return;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    scrollerTop.style.display = scrollTop > 150 ? 'flex' : 'none';
    const nearBottom =
      window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 150;
    scrollerBottom.style.display = nearBottom ? 'none' : 'flex';
  }

  scrollerTop?.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  scrollerBottom?.addEventListener('click', () => {
    listEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
    window.scrollTo({
      top: document.documentElement.scrollHeight - window.innerHeight,
      behavior: 'smooth',
    });
  });

  window.addEventListener('scroll', updateScrollerVisibility);
  window.addEventListener('resize', updateScrollerVisibility);
  updateScrollerVisibility();
}

/**
 * Load and render carousels for each room card
 */
async function loadRoomCarousels(): Promise<void> {
  try {
    const tenantId = getTenantId();
    const hotelCode = getHotelCode();
    const containerEls = Array.from(document.querySelectorAll('.room-images')) as HTMLElement[];

    if (containerEls.length === 0) return;

    for (const el of containerEls) {
      const roomType = el.getAttribute('data-room-type');
      if (!roomType) continue;

        try {
          const images = await getRoomImages(tenantId, hotelCode, roomType);
          if (images && images.length > 0) {
            renderCarousel(el, images.slice(0, 15));
          } else {
          // Show default placeholder
          el.innerHTML = `
            <div class="room-image-placeholder">
              <span class="placeholder-icon">🏨</span>
            </div>
          `;
        }
      } catch (err) {
        console.warn('[RoomTypes] Failed to fetch images for', roomType, err);
      }
    }
  } catch (err) {
    console.error('[RoomTypes] loadRoomCarousels error', err);
  }
}

/**
 * Render a simple swipeable carousel into container element
 */
function renderCarousel(
  container: HTMLElement,
  images: Array<{ path: string; url: string; thumbUrl?: string; order?: number }>
): void {
  if (!images || images.length === 0) return;

  // Single image case
  if (images.length === 1) {
    const img = images[0];
    container.innerHTML = `
      <div class="carousel">
        <div class="carousel-track">
          <div class="carousel-item" data-real-index="0">
            <img src="${img.thumbUrl || img.url}" alt="room image" loading="lazy" />
          </div>
        </div>
      </div>
    `;
    const itemEl = container.querySelector('.carousel-item') as HTMLElement;
    itemEl?.addEventListener('click', () => openLightbox(images, 0));
    return;
  }

  // Multiple images: Create Infinite Loop track with Clones
  // Item sequence: [ Clone of N-1, Img 0, Img 1, ..., Img N-1, Clone of 0 ]
  const totalCount = images.length;
  const cloneLast = images[totalCount - 1];
  const cloneFirst = images[0];

  const trackItems = [
    { ...cloneLast, isClone: true, realIndex: totalCount - 1 },
    ...images.map((img, i) => ({ ...img, isClone: false, realIndex: i })),
    { ...cloneFirst, isClone: true, realIndex: 0 },
  ];

  container.innerHTML = `
    <div class="carousel">
      <div class="carousel-track">
        ${trackItems
          .map(
            (img) => `
          <div class="carousel-item" data-real-index="${img.realIndex}">
            <img data-src="${img.thumbUrl || img.url}" alt="room image" loading="lazy" />
          </div>
        `
          )
          .join('')}
      </div>
      <button class="carousel-prev" aria-label="Previous">‹</button>
      <button class="carousel-next" aria-label="Next">›</button>
      <div class="carousel-indicators">
        ${images.map((_, i) => `<button class="indicator${i === 0 ? ' active' : ''}" data-index="${i}"></button>`).join('')}
      </div>
    </div>
  `;

  const track = container.querySelector('.carousel-track') as HTMLElement;
  const prev = container.querySelector('.carousel-prev') as HTMLElement;
  const next = container.querySelector('.carousel-next') as HTMLElement;
  const indicators = Array.from(container.querySelectorAll('.indicator')) as HTMLElement[];
  if (!track) return;

  // Lazy-load images
  track.querySelectorAll('img[data-src]').forEach((imgEl: Element) => {
    const img = imgEl as HTMLImageElement;
    img.src = img.dataset.src || '';
  });

  const getItemWidth = () => {
    return (track.querySelector('.carousel-item') as HTMLElement)?.clientWidth || track.clientWidth;
  };

  // Initial scroll position: start at index 1 (the first real image)
  requestAnimationFrame(() => {
    const itemWidth = getItemWidth();
    track.scrollLeft = itemWidth;
  });

  let isTeleporting = false;

  const handleScroll = () => {
    if (isTeleporting) return;
    const itemWidth = getItemWidth();
    if (!itemWidth) return;

    const currentTrackIndex = Math.round(track.scrollLeft / itemWidth);

    // Instant Teleport when reaching clones
    if (currentTrackIndex <= 0) {
      isTeleporting = true;
      track.scrollLeft = totalCount * itemWidth;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === totalCount - 1));
      setTimeout(() => {
        isTeleporting = false;
      }, 50);
    } else if (currentTrackIndex >= totalCount + 1) {
      isTeleporting = true;
      track.scrollLeft = 1 * itemWidth;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === 0));
      setTimeout(() => {
        isTeleporting = false;
      }, 50);
    } else {
      const realIndex = currentTrackIndex - 1;
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === realIndex));
    }
  };

  track.addEventListener('scroll', handleScroll, { passive: true });

  // Next & Prev Arrow Clicks
  next?.addEventListener('click', (e) => {
    e.stopPropagation();
    const itemWidth = getItemWidth();
    track.scrollBy({ left: itemWidth, behavior: 'smooth' });
  });

  prev?.addEventListener('click', (e) => {
    e.stopPropagation();
    const itemWidth = getItemWidth();
    track.scrollBy({ left: -itemWidth, behavior: 'smooth' });
  });

  // Open Lightbox on image click
  const items = Array.from(track.querySelectorAll('.carousel-item')) as HTMLElement[];
  items.forEach((itemEl) => {
    itemEl.addEventListener('click', () => {
      const idx = parseInt(itemEl.getAttribute('data-real-index') || '0', 10);
      openLightbox(images, idx);
    });
  });

  // Indicator clicks
  indicators.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const realIdx = parseInt(btn.getAttribute('data-index') || '0', 10);
      const itemWidth = getItemWidth();
      track.scrollTo({ left: (realIdx + 1) * itemWidth, behavior: 'smooth' });
    });
  });
}

// Extend window for global handlers
declare global {
  interface Window {
    goToBooking: (index: number) => void;
  }
}
